import React from "react";
import TableHeader from "./TableHeader";
import Pagination from "./Pagination";
import { UI_MESSAGES } from "../../config/constants";

/**
 * Reusable DataTable component
 *
 * Supports two usage patterns:
 *
 * 1. Column-based (auto-render): Pass `columns` with `accessor` (string key or function)
 *    <DataTable columns={[{header:"Name", accessor:"name"}]} data={items} />
 *
 * 2. Custom render: Pass `renderRow` function for full control
 *    <DataTable columns={columns} data={items} renderRow={(item, i) => <tr>...</tr>} />
 */
export default function DataTable({
  columns,
  data = [],
  renderRow,
  sorting = {},
  pagination = {},
  checkbox = { show: false },
  loading = false,
  emptyMessage = UI_MESSAGES.EMPTY_DATA,
}) {
  const { sortField, sortOrder, onSort } = sorting;
  const { page, totalPages, onPageChange } = pagination;
  const { show: showCheckbox, onCheckAll, isAllChecked } = checkbox;

  const totalColumns = columns.length + (showCheckbox ? 1 : 0);

  // Auto-render row from columns config when renderRow is not provided
  const defaultRenderRow = (item, index) => (
    <tr key={item.id || index} className="hover:bg-gray-50 transition">
      {columns.map((col, colIndex) => {
        let cellContent;
        if (typeof col.accessor === "function") {
          cellContent = col.accessor(item, index);
        } else if (typeof col.accessor === "string") {
          cellContent = item[col.accessor] ?? "-";
        } else {
          cellContent = "-";
        }

        return (
          <td
            key={col.field || col.header || colIndex}
            className={`px-4 py-3 ${col.cellClassName || ""}`}
          >
            {cellContent}
          </td>
        );
      })}
    </tr>
  );

  const rowRenderer = renderRow || defaultRenderRow;

  return (
    <>
      <div className="relative overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200/60">
        <table className="w-full text-sm text-left text-gray-700">
          <TableHeader
            columns={columns}
            onSort={onSort}
            sortField={sortField}
            sortOrder={sortOrder}
            showCheckbox={showCheckbox}
            onCheckAll={onCheckAll}
            isAllChecked={isAllChecked}
          />

          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={totalColumns} className="p-12 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 border-4 border-gray-200 rounded-full"></div>
                      <div className="w-12 h-12 border-4 border-t-indigo-600 border-r-indigo-500 rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-600 tracking-wide">
                      Memuat data...
                    </span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={totalColumns}
                  className="p-12 text-center text-gray-500 font-medium tracking-wide"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => rowRenderer(item, index))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - rendered outside the card */}
      {pagination && totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
}
